import { useState } from 'react';
import { Check, ChevronsUpDown, User, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatName } from '@/lib/name';
import { getEmailDisplay } from '@/types/user';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';

interface OrganizationMember {
  id: string;
  name: string;
  email: string;
  type: 'individual';
}

interface Group {
  id: string;
  name: string;
  memberCount: number;
  type: 'group';
}

export type SelectableItem = OrganizationMember | Group;

interface MemberGroupSelectorProps {
  value?: SelectableItem;
  onValueChange: (value: SelectableItem | undefined) => void;
  placeholder?: string;
  className?: string;
  size?: 'default' | 'sm' | 'lg';
  showAnyOption?: boolean;
  members?: OrganizationMember[];
  groups?: Group[];
}

// Mock data for demonstration
const mockMembers: OrganizationMember[] = [
  { id: '1', name: 'John Doe', email: 'john@company.com', type: 'individual' },
  {
    id: '2',
    name: 'Jane Smith',
    email: 'jane@company.com',
    type: 'individual',
  },
  {
    id: '3',
    name: 'Mike Johnson',
    email: 'mike@company.com',
    type: 'individual',
  },
  {
    id: '4',
    name: 'Sarah Wilson',
    email: 'sarah@company.com',
    type: 'individual',
  },
];

const mockGroups: Group[] = [
  { id: 'g1', name: 'Finance Team', memberCount: 5, type: 'group' },
  { id: 'g2', name: 'Executive Committee', memberCount: 3, type: 'group' },
  { id: 'g3', name: 'Operations Team', memberCount: 8, type: 'group' },
  { id: 'g4', name: 'Security Team', memberCount: 4, type: 'group' },
];

export function MemberGroupSelector({
  value,
  onValueChange,
  placeholder = 'Select a person or group',
  className,
  size = 'default',
  showAnyOption = true,
  members = mockMembers,
  groups = mockGroups,
}: MemberGroupSelectorProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<'any' | 'members' | 'groups'>('any');

  const getFilteredItems = (): SelectableItem[] => {
    switch (filter) {
      case 'members':
        return members;
      case 'groups':
        return groups;
      case 'any':
      default:
        return [...members, ...groups];
    }
  };

  const renderItem = (item: SelectableItem) => {
    if (item.type === 'individual') {
      return (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <div className="flex flex-col">
            <span>{formatName(item.name)}</span>
            <span className="text-xs text-muted-foreground">
              {getEmailDisplay(item.email)}
            </span>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <div className="flex flex-col">
            <span>{item.name}</span>
            <span className="text-xs text-muted-foreground">
              {item.memberCount} member{item.memberCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      );
    }
  };

  const getDisplayValue = () => {
    if (!value) {
      return showAnyOption ? 'Any organization member' : placeholder;
    }

    if (value.type === 'individual') {
      return formatName(value.name);
    } else {
      return `${value.name} (${value.memberCount} members)`;
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          size={size}
          className={cn(
            'w-full justify-between',
            'border-input',
            'px-4',
            className
          )}
        >
          <span
            className={cn(
              'flex-1 text-left',
              !value && !showAnyOption ? 'text-muted-foreground' : ''
            )}
          >
            {getDisplayValue()}
          </span>
          <div className="flex items-center gap-1">
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={`Search ${placeholder.toLowerCase()}...`}
          />
          <div className="px-2 py-2 border-b">
            <Tabs
              value={filter}
              onValueChange={(value: string) =>
                setFilter(value as typeof filter)
              }
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="any">All</TabsTrigger>
                <TabsTrigger value="members">People</TabsTrigger>
                <TabsTrigger value="groups">Groups</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup className="h-80 overflow-y-auto">
            {showAnyOption && (
              <>
                <CommandItem
                  onSelect={() => {
                    onValueChange(undefined);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      !value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div className="flex flex-col">
                      <span>Any organization member</span>
                      <span className="text-xs text-muted-foreground">
                        Default option
                      </span>
                    </div>
                  </div>
                </CommandItem>
                <Separator className="w-full my-2" />
              </>
            )}
            {getFilteredItems().map((item) => (
              <CommandItem
                key={item.id}
                onSelect={() => {
                  onValueChange(value?.id === item.id ? undefined : item);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value?.id === item.id ? 'opacity-100' : 'opacity-0'
                  )}
                />
                {renderItem(item)}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
